import { create } from 'zustand';
import { attendanceService, paymentService } from '@/services';
import type {
  AttendanceRecord,
  AttendanceSummary,
  ClockInData,
  ClockInResponse,
  ClockOutData,
  ClockOutResponse,
  Payment,
  TodayAttendance,
} from '@/types';
import { extractErrorMessage, getCurrentYearMonth } from '@/utils';

const refreshAttendanceData = async () => {
  const { year, month } = getCurrentYearMonth();
  const [todayData, records, summaryData] = await Promise.all([
    attendanceService.getTodayAttendance(),
    attendanceService.getMyAttendance(),
    attendanceService.getMySummary(year, month),
  ]);
  return { todayData, records, summaryData };
};

interface AttendanceState {
  todayAttendance: TodayAttendance | null;
  attendanceRecords: AttendanceRecord[];
  summary: AttendanceSummary | null;
  paymentHistory: Payment[];
  currentPayment: Payment | null;
  isLoading: boolean;
  error: string | null;

  fetchTodayAttendance: () => Promise<void>;
  clockIn: (data: ClockInData) => Promise<ClockInResponse>;
  clockOut: (data: ClockOutData) => Promise<ClockOutResponse>;
  fetchMyAttendance: (startDate?: string, endDate?: string) => Promise<void>;
  fetchMySummary: (year: number, month: number) => Promise<void>;
  fetchPaymentRecord: () => Promise<void>;
  clearError: () => void;
}

export const useAttendanceStore = create<AttendanceState>()((set) => ({
  todayAttendance: null,
  attendanceRecords: [],
  summary: null,
  paymentHistory: [],
  currentPayment: null,
  isLoading: false,
  error: null,

  fetchTodayAttendance: async () => {
    set({ isLoading: true, error: null });
    try {
      const data = await attendanceService.getTodayAttendance();
      set({ todayAttendance: data, isLoading: false });
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to fetch attendance');
      set({ error: message, isLoading: false });
    }
  },

  clockIn: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await attendanceService.clockIn(data);
      const { todayData, records, summaryData } = await refreshAttendanceData();
      set({
        todayAttendance: todayData,
        attendanceRecords: records,
        summary: summaryData,
        isLoading: false,
      });
      return response;
    } catch (error) {
      const message = extractErrorMessage(error, 'Clock-in failed');
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  clockOut: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await attendanceService.clockOut(data);
      const { todayData, records, summaryData } = await refreshAttendanceData();
      set({
        todayAttendance: todayData,
        attendanceRecords: records,
        summary: summaryData,
        isLoading: false,
      });
      return response;
    } catch (error) {
      const message = extractErrorMessage(error, 'Clock-out failed');
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  fetchMyAttendance: async (startDate, endDate) => {
    set({ isLoading: true, error: null });
    try {
      const records = await attendanceService.getMyAttendance(startDate, endDate);
      set({ attendanceRecords: records, isLoading: false });
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to fetch attendance records');
      set({ error: message, isLoading: false });
    }
  },

  fetchMySummary: async (year, month) => {
    set({ isLoading: true, error: null });
    try {
      const summary = await attendanceService.getMySummary(year, month);
      set({ summary, isLoading: false });
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to fetch summary');
      set({ error: message, isLoading: false });
    }
  },

  fetchPaymentRecord: async () => {
    set({ isLoading: true, error: null });
    try {
      const payments = await paymentService.getMyPayments();
      const sortedPayments = [...payments].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
      const now = new Date();
      const currentMonthPayment = payments.find(
        (p) => p.year === now.getFullYear() && p.month === now.getMonth() + 1,
      );
      set({
        paymentHistory: sortedPayments,
        currentPayment: currentMonthPayment || null,
        isLoading: false,
      });
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to fetch payment records');
      set({ error: message, isLoading: false });
    }
  },

  clearError: () => set({ error: null }),
}));

export default useAttendanceStore;
